import merge from 'lodash.merge';
import { resolve } from 'path';
import { PartialProjectConfig, ProjectConfig } from '../../types';
import logger from '../logger';
import loadConfig from './loadConfig';
import { BuildFlags } from '../../commands/build/utilities/types';

export default function computeFullProjectConfig(
  partialConfig: PartialProjectConfig,
  projectRoot: string,
  flags?: { build?: Pick<BuildFlags, 'ignoreExtendsErrors'> },
): ProjectConfig {
  const resolvedConfig = partialConfig as PartialProjectConfigWithExtends;

  if (!resolvedConfig.extends) {
    logger.debug('No extends field, returning partial config');
    return resolvedConfig as ProjectConfig;
  } else {
    logger.debug('Extends field found, resolving');
    const parentConfigPath = resolve(projectRoot, resolvedConfig.extends);
    const parentConfig = loadConfig(parentConfigPath);

    parentConfig.appPath = parentConfig.appPath || '.';

    const parentFullConfig = computeFullProjectConfig(
      parentConfig,
      projectRoot,
      flags,
    );

    // skip if `flags.build` isn't defined since --ignore-extends-errors is only available via todesktop-build.
    if (flags?.build && !flags.build.ignoreExtendsErrors) {
      const hasSameId = resolvedConfig.id === parentFullConfig.id;
      const hasSameAppId =
        resolvedConfig.appId &&
        parentFullConfig.appId &&
        resolvedConfig.appId === parentFullConfig.appId;

      if (hasSameId || hasSameAppId) {
        throw new Error(`
todesktop.json invalid. Cannot have the same "id" or "appId" as an extended todesktop.json file.

You can disable this error by running todesktop build with the --ignore-extends-errors flag.`);
      }

      // throw error in this case otherwise the child config will inherit the defined appId from the parent
      if (parentFullConfig.appId && !resolvedConfig.appId) {
        throw new Error(`
todesktop.json invalid. Please add "appId" to your todesktop.json or remove "appId" from the extended todesktop.json.

You can disable this error by running todesktop build with the --ignore-extends-errors flag.`);
      }
    }

    const result = merge({}, parentFullConfig, resolvedConfig);
    delete result.extends;
    return result;
  }
}

type PartialProjectConfigWithExtends = PartialProjectConfig & {
  appId?: string;
  appPath?: string;
  extends?: string;
  id?: string;
};
